/**
 * FERP Portal - Regulatory Source Monitor Page
 *
 * Displays the status of regulatory source URLs tracked by check-regulatory-sources.sh.
 * Detects content drift in statute PDFs, CFR sections, and MDH guidance documents.
 *
 * Currently static — the bash script writes SHA-256 hashes to .ferp-reports/
 * (gitignored). A backend GraphQL query is planned but not yet implemented.
 *
 * DRY: This monitoring pattern applies to any config type with external URL references.
 * When the backend query ships, replace KNOWN_SOURCES with live data.
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - Dumb Frontend
 * - Admin view layer only
 */

import {
  Alert,
  Anchor,
  Badge,
  Card,
  Container,
  Group,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconExternalLink,
  IconInfoCircle,
  IconShieldCheck,
} from '@tabler/icons-react';

const KNOWN_SOURCES = [
  {
    id: 'mn-144g',
    name: 'MN Statute 144G — Assisted Living',
    url: 'https://www.revisor.mn.gov/statutes/cite/144G',
    authority: 'MN Revisor',
    lastChecked: '2026-03-28',
    status: 'current' as const,
  },
  {
    id: 'mn-245c',
    name: 'MN Statute 245C — Background Studies',
    url: 'https://www.revisor.mn.gov/statutes/cite/245C',
    authority: 'MN Revisor',
    lastChecked: '2026-03-28',
    status: 'current' as const,
  },
  {
    id: 'mn-626557',
    name: 'MN Statute 626.557 — Vulnerable Adults Reporting',
    url: 'https://www.revisor.mn.gov/statutes/cite/626.557',
    authority: 'MN Revisor',
    lastChecked: '2026-03-28',
    status: 'current' as const,
  },
  {
    id: 'cfr-42-483',
    name: 'CFR 42 Part 483 — Requirements for Long Term Care',
    url: 'https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-G/part-483',
    authority: 'Federal CFR',
    lastChecked: '2026-03-28',
    status: 'current' as const,
  },
  {
    id: 'mn-dhs-alf-manual',
    name: 'MN DHS ALF Licensing Manual',
    url: 'https://www.dhs.state.mn.us/main/idcplg?IdcService=GET_DYNAMIC_CONVERSION&RevisionSelectionMethod=LatestReleased&dDocName=id_000812',
    authority: 'MN DHS',
    lastChecked: '2026-03-28',
    status: 'current' as const,
  },
  {
    id: 'flsa',
    name: 'Fair Labor Standards Act (FLSA)',
    url: 'https://www.dol.gov/agencies/whd/flsa',
    authority: 'US DOL',
    lastChecked: '2026-03-28',
    status: 'current' as const,
  },
];

const STATUS_COLORS: Record<string, string> = {
  current: 'green',
  changed: 'red',
  error: 'orange',
  unknown: 'gray',
};

const STATUS_LABELS: Record<string, string> = {
  current: 'No Change',
  changed: 'Content Changed',
  error: 'Check Failed',
  unknown: 'Not Checked',
};

export function RegulatorySourceMonitorPage() {
  const currentCount = KNOWN_SOURCES.filter((s) => (s.status as string) === 'current').length;
  const changedCount = KNOWN_SOURCES.filter((s) => (s.status as string) === 'changed').length;

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Title order={2}>Regulatory Source Monitor</Title>
            <Text c="dimmed" size="sm">
              SHA-256 content drift detection for statute URLs, CFR sections, and guidance documents
            </Text>
          </div>
          <Group gap="xs">
            <Badge variant="light" color="green" size="lg">{currentCount} current</Badge>
            {changedCount > 0 && (
              <Badge variant="light" color="red" size="lg">{changedCount} changed</Badge>
            )}
          </Group>
        </Group>

        <Alert
          icon={<IconInfoCircle size={16} />}
          color="blue"
          title="Offline Detection"
        >
          Source monitoring runs via <Text span ff="monospace" size="sm">check-regulatory-sources.sh</Text>{' '}
          (cron or manual). Hashes are stored in <Text span ff="monospace" size="sm">.ferp-reports/</Text>.
          A backend GraphQL query is planned. When content changes, rulesets referencing that source
          are flagged for architect review.
        </Alert>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
          <Card withBorder p="sm">
            <Group gap="xs">
              <ThemeIcon variant="light" color="green" size="sm">
                <IconShieldCheck size={14} />
              </ThemeIcon>
              <Text size="sm" fw={500}>{currentCount} sources unchanged</Text>
            </Group>
          </Card>
          <Card withBorder p="sm">
            <Group gap="xs">
              <ThemeIcon variant="light" color={changedCount > 0 ? 'red' : 'gray'} size="sm">
                <IconAlertTriangle size={14} />
              </ThemeIcon>
              <Text size="sm" fw={500}>{changedCount} sources with content drift</Text>
            </Group>
          </Card>
        </SimpleGrid>

        <Stack gap="sm">
          {KNOWN_SOURCES.map((source) => (
            <Card key={source.id} withBorder p="md">
              <Group justify="space-between" mb="xs">
                <Group gap="sm">
                  <div>
                    <Text fw={500} size="sm">{source.name}</Text>
                    <Text size="xs" c="dimmed" ff="monospace">{source.id}</Text>
                  </div>
                </Group>
                <Group gap="xs">
                  <Badge variant="light" color="gray" size="xs">{source.authority}</Badge>
                  <Badge variant="light" color={STATUS_COLORS[source.status]} size="sm">
                    {STATUS_LABELS[source.status]}
                  </Badge>
                </Group>
              </Group>
              <Group justify="space-between">
                <Anchor size="xs" href={source.url} target="_blank">
                  <Group gap={4}>
                    <IconExternalLink size={12} />
                    {source.url.length > 80 ? source.url.slice(0, 80) + '...' : source.url}
                  </Group>
                </Anchor>
                <Text size="xs" c="dimmed">Last checked: {source.lastChecked}</Text>
              </Group>
            </Card>
          ))}
        </Stack>
      </Stack>
    </Container>
  );
}

export default RegulatorySourceMonitorPage;
